import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { listSurveys } from "@/api/surveys";
import { useAuth } from "@/hooks/useAuth";
import type { Survey } from "@/types";

export function Home() {
  const { data, isLoading } = useQuery({
    queryKey: ["surveys"],
    queryFn: listSurveys,
  });
  const { user } = useAuth();

  const surveys = data?.surveys;
  const canCreateSurvey = data?.can_create_survey ?? false;
  const remainingCredits = data?.remaining_credits ?? null;
  const subscriptionActive = data?.subscription_active ?? false;

  return (
    <DashboardLayout>
      {/* Hero Section */}
      <div className="mb-10">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-6 text-(--primary-color)">
          Welcome {user?.email || ""}
        </h1>
        <div className="rounded-2xl bg-linear-to-br from-primary/10 via-primary/5 to-background p-8 border border-primary/10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
                Your Surveys
              </h1>
              <p className="mt-2 text-lg text-muted-foreground max-w-lg">
                Manage your shed approval projects. Upload surveys, customize
                your plans, and get HOA-ready documents.
              </p>
              {!subscriptionActive && remainingCredits !== null && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2">
                  <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-sm font-medium text-primary">
                    {remainingCredits} survey{" "}
                    {remainingCredits === 1 ? "credit" : "credits"} remaining
                  </span>
                </div>
              )}
            </div>
            {canCreateSurvey && (
              <Button
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30 hover:cursor-pointer"
              >
                <Link to="/surveys/new" className="gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  New Survey
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Survey List */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="h-10 w-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
          <p className="mt-4 text-muted-foreground">Loading your surveys...</p>
        </div>
      ) : surveys && surveys.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">
              Recent Projects
            </h2>
            <span className="text-sm text-muted-foreground">
              {surveys.length} {surveys.length === 1 ? "survey" : "surveys"}
            </span>
          </div>
          {surveys.map((survey: Survey) => (
            <a
              key={survey.id}
              href={survey.pdf_url ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Card className="group hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 cursor-pointer border-primary/10 hover:border-primary/30 overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex items-stretch">
                    {/* Status indicator bar */}
                    <div className="w-1.5 bg-primary" />
                    <div className="flex-1 flex items-center justify-between p-5">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-primary"
                          >
                            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                            {survey.customer_name}
                          </p>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {survey.customer_phone}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="mt-1.5 text-xs text-muted-foreground">
                            {new Date(survey.inserted_at).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </p>
                        </div>
                        <div className="text-muted-foreground group-hover:text-primary transition-colors">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m9 18 6-6-6-6" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      ) : (
        <Card className="border-dashed border-2 border-primary/20 bg-primary/5">
          <CardContent className="py-16 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-primary"
              >
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No surveys yet
            </h3>
            <p className="text-muted-foreground max-w-sm mx-auto">
              {canCreateSurvey
                ? "Upload your first survey to start creating HOA-ready approval documents."
                : "No survey credits remaining. Purchase a credit to upload a new survey."}
            </p>
            {canCreateSurvey && (
              <Button
                asChild
                className="mt-6 bg-primary hover:bg-primary/90 hover:cursor-pointer"
              >
                <Link to="/surveys/new">Upload Your First Survey</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
}
